import React, { useState, useEffect, useMemo } from "react";
import { resetCourseList } from "../../state/actions/getAllCourseListByTeacherAction";
import { student_profile } from "../../assets/images/images";
import {
  getOverallPerformanceScoreByTeacher,
  resetAverageScore,
} from "../../state/actions/getOverallPerformanceScoreByTeacherAction";
import { resetEventData } from "../../state/actions/getEventOverviewByTeacherAction";
import { resetCourseListStudent } from "../../state/actions/getAllCourseListByStudentAction";

import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext";
import { Nostudentinvite } from "../../assets/images/images";
import CourseListLoader from "./CourseListLoader";

const StudentSelectfilter = ({
  selectedCourseData,
  periodTab,
  year,
  month,
  setSelectedStudentData,
  start_date,
  end_date,
  month_year,
  value,
  isAIGenerating,
}) => {
  const dispatch = useDispatch();
  const { token } = useAuth();

  const globalState = useSelector((state) => state?.globalState);
  const teacher_id = globalState?.teacher_details?.data?.teacher_id;
  const role = globalState?.role;

  const [studentsActiveTab, setStudentsActiveTab] = useState("my_students");

  const teacherCourseState = useSelector(
    (state) => state?.getAllCourseListByTeacher || {},
  );
  const isCourseListLoading = teacherCourseState?.isLoading || false;

  // Memoize all arrays that are used as useEffect dependencies.
  //    Without useMemo, the `|| []` fallback creates a NEW array reference on
  //    every render (even when data hasn't changed), causing useEffects that
  //    depend on these arrays to re-run infinitely.

  const coursesRaw = teacherCourseState?.courses;
  const courses = useMemo(() => coursesRaw || [], [coursesRaw]);

  const departmentCoursesRaw = useSelector(
    (state) =>
      state?.getAllCourseListByTeacher?.department_role_data?.department_courses,
  );
  const departmentCourses = useMemo(
    () => departmentCoursesRaw || [],
    [departmentCoursesRaw],
  );

  const assignedStudentsRaw =
    teacherCourseState?.department_role_data?.assigned_students;
  const assignedStudents = useMemo(
    () => assignedStudentsRaw || [],
    [assignedStudentsRaw],
  );

  // Also memoize the combined lookup array — it's used inside useEffects
  //    and without memoization [...courses, ...departmentCourses] always
  //    produces a new reference even when both inputs are unchanged.
  const allCoursesForStudentLookup = useMemo(
    () => [...courses, ...departmentCourses],
    [courses, departmentCourses],
  );

  const [searchStudent, setsearchStudent] = useState("");
  const [studentData, setstudentData] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  const getCurrentDateFormatted = () => {
    const today = new Date();
    return `${today.getDate().toString().padStart(2, "0")}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${today.getFullYear()}`;
  };

  const getWeekAgoDateFormatted = () => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return `${weekAgo.getDate().toString().padStart(2, "0")}-${(
      weekAgo.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${weekAgo.getFullYear()}`;
  };

  const getOverallPerformanceScoreByTeacherRequest = (studentsOverride) => {
    const studentsToUse = studentsOverride ?? selectedStudents;
    const studentsString = studentsToUse.join(",");
    const coursesString = selectedCourseData.join(",");

    if (!coursesString || !studentsString) return;

    let monthYearValue = month_year;
    if (!monthYearValue && month && year) {
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const validMonth = parseInt(month, 10);
      if (!isNaN(validMonth) && validMonth >= 1 && validMonth <= 12) {
        monthYearValue = `${months[validMonth - 1]}-${year}`;
      }
    }

    let customStartDate = start_date;
    let customEndDate = end_date;
    if (periodTab === "custom") {
      if (!customStartDate) customStartDate = getWeekAgoDateFormatted();
      if (!customEndDate) customEndDate = getCurrentDateFormatted();
    }

    const data = {
      teacher_id,
      time_period: periodTab,
      courses: coursesString,
      students: studentsString,
      ...((periodTab === "week" ||
        periodTab === "month" ||
        periodTab === "year") && { month, month_year: monthYearValue }),
      ...(periodTab !== "custom" && { year }),
      ...(periodTab === "custom" && {
        start_date: customStartDate,
        end_date: customEndDate,
      }),
    };

    dispatch(getOverallPerformanceScoreByTeacher(token, data));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAverageScore());
      dispatch(resetEventData());
      dispatch(resetCourseList());
      dispatch(resetCourseListStudent());
    };
  }, []);

  // FILTER useEffect — determines which students appear in the visible list
  useEffect(() => {
    let studentsToUse = [];
    const myStudents = courses.flatMap((course) => course?.students || []);

    if (selectedCourseData.length > 0) {
      const studentMap = new Map();

      for (const courseItem of allCoursesForStudentLookup) {
        if (
          selectedCourseData.includes(courseItem.course_id) &&
          courseItem.students
        ) {
          for (const student of courseItem.students) {
            if (!studentMap.has(student.student_id)) {
              studentMap.set(student.student_id, {
                student_id: student.student_id,
                student_name: student.student_name,
                img_url: student.img_url,
              });
            }
          }
        }
      }

      studentsToUse = Array.from(studentMap.values());
    }

    if (studentsToUse.length > 0) {
      const isMyStudents = studentsActiveTab === "my_students";
      const sourceList = isMyStudents ? myStudents : assignedStudents;
      const ids = new Set(sourceList.map((obj) => obj?.student_id));
      studentsToUse = studentsToUse.filter((obj) => ids.has(obj?.student_id));
    }

    const filtered = studentsToUse.filter((s) =>
      s.student_name.toLowerCase().includes(searchStudent.toLowerCase()),
    );
    setstudentData(filtered);
  }, [
    searchStudent,
    studentsActiveTab,
    selectedCourseData,
    courses,             // ✅ stable reference thanks to useMemo
    departmentCourses,   // ✅ stable reference thanks to useMemo
    assignedStudents,    // ✅ stable reference thanks to useMemo
    allCoursesForStudentLookup, // ✅ stable reference thanks to useMemo
  ]);

  // AUTO-SELECT useEffect — selects all students and triggers the API
  useEffect(() => {
    let allStudentIds = [];
    const myStudents = courses.flatMap((course) => course?.students || []);

    if (selectedCourseData.length > 0) {
      const map = new Map();
      for (const courseItem of allCoursesForStudentLookup) {
        if (
          selectedCourseData.includes(courseItem.course_id) &&
          courseItem.students
        ) {
          for (const student of courseItem.students) {
            if (!map.has(student.student_id)) {
              map.set(student.student_id, {
                student_id: student.student_id,
                student_name: student.student_name,
                img_url: student.img_url,
              });
            }
          }
        }
      }
      allStudentIds = Array.from(map.values()).map((s) => s.student_id);
    }

    if (allStudentIds.length > 0) {
      const isMyStudents = studentsActiveTab === "my_students";
      const sourceList = isMyStudents ? myStudents : assignedStudents;
      const ids = new Set(sourceList.map((obj) => obj?.student_id));
      allStudentIds = allStudentIds.filter((id) => ids.has(id));
    }

    if (
      JSON.stringify([...allStudentIds].sort()) !==
      JSON.stringify([...selectedStudents].sort())
    ) {
      setSelectedStudents(allStudentIds);
      setSelectedStudentData(allStudentIds);
    }

    if (
      selectedCourseData.length > 0 &&
      role === "Teacher" &&
      value === "1" &&
      allStudentIds.length > 0
    ) {
      getOverallPerformanceScoreByTeacherRequest(allStudentIds);
    }
  }, [
    studentsActiveTab,
    selectedCourseData,
    assignedStudents,          // ✅ stable reference thanks to useMemo
    periodTab,
    year,
    month,
    start_date,
    end_date,
    month_year,
    courses,                   // ✅ stable reference thanks to useMemo
    departmentCourses,         // ✅ stable reference thanks to useMemo
    allCoursesForStudentLookup, // ✅ stable reference thanks to useMemo
  ]);

  // API call useEffect — fires when selectedStudents changes from manual check/uncheck
  useEffect(() => {
    if (
      selectedCourseData.length > 0 &&
      role === "Teacher" &&
      value === "1" &&
      selectedStudents.length > 0
    ) {
      getOverallPerformanceScoreByTeacherRequest();
    }
  }, [
    selectedStudents,
    selectedCourseData,
    periodTab,
    year,
    month,
    start_date,
    end_date,
    month_year,
    role,
    value,
    studentsActiveTab,
  ]);

  const handleCheck = (student_id) => {
    if (isAIGenerating) return;
    const newSelection = selectedStudents.includes(student_id)
      ? selectedStudents.filter((id) => id !== student_id)
      : [...selectedStudents, student_id];
    setSelectedStudents(newSelection);
    setSelectedStudentData(newSelection);
  };

  const handleSelectAll = () => {
    if (isAIGenerating) return;
    const visibleIds = studentData.map((s) => s.student_id);
    const allSelected = visibleIds.every((id) => selectedStudents.includes(id));
    const newSelection = allSelected
      ? selectedStudents.filter((id) => !visibleIds.includes(id))
      : [...new Set([...selectedStudents, ...visibleIds])];
    setSelectedStudents(newSelection);
    setSelectedStudentData(newSelection);
  };

  const handleDeselectAllStudent = () => {
    if (isAIGenerating) return;
    setSelectedStudents([]);
    setSelectedStudentData([]);
  };

  const userRole = globalState?.teacher_details?.data?.teacher_department_roles;
  const hideTabs = ["advisor", "learning specialist", "hod"].includes(userRole);

  return (
    <div
      className="courses-select-filter"
      style={{
        opacity: isAIGenerating ? 0.5 : 1,
        pointerEvents: isAIGenerating ? "none" : "auto",
      }}
    >
      <div className="courses-select-filter-header">
        <h2>Students</h2>
        <p>{selectedStudents.length} Selected</p>
      </div>
      {hideTabs && (
        <div className="students-select-tabs">
          <button
            className={studentsActiveTab === "my_students" ? "active" : ""}
            onClick={() => setStudentsActiveTab("my_students")}
          >
            My Students
          </button>
          <button
            className={
              studentsActiveTab === "assigned_students" ? "active" : ""
            }
            onClick={() => setStudentsActiveTab("assigned_students")}
          >
            Assigned Students
          </button>
        </div>
      )}
      <div className="courses-select-filter-serach">
        <i className="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          placeholder="Search for a student..."
          value={searchStudent}
          onChange={(e) => setsearchStudent(e.target.value)}
        />
      </div>
      <div className="courses-select-filter-results">
        {isCourseListLoading ? (
          <CourseListLoader
            messages={[
              "Fetching student list",
              "Loading student data",
              "Preparing student list",
              "Organizing student information",
              "Almost ready",
              "Getting students ready",
              "Just a moment more",
            ]}
          />
        ) : studentData.length > 0 ? (
          <>
            <div className="Select-all">
              <input
                type="checkbox"
                checked={
                  studentData.length > 0 &&
                  studentData.every((s) =>
                    selectedStudents.includes(s.student_id),
                  )
                }
                onChange={handleSelectAll}
                disabled={studentData.length === 0}
              />
              <label
                htmlFor="select-all"
                onClick={handleSelectAll}
                disabled={studentData.length === 0}
                style={
                  studentData.length === 0
                    ? { cursor: "not-allowed", color: "#999" }
                    : { cursor: "pointer" }
                }
              >
                Select All
              </label>
              <button
                className="deselect-all-btn"
                onClick={handleDeselectAllStudent}
                disabled={studentData.length === 0}
                style={
                  studentData.length === 0
                    ? { cursor: "not-allowed", color: "#999" }
                    : {}
                }
              >
                Clear All
              </button>
            </div>
            <div className="courses-select-filter-folder">
              {studentData.map((student) => (
                <div className="course-item" key={student.student_id}>
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.student_id)}
                    onChange={() => handleCheck(student.student_id)}
                  />
                  <img
                    src={student.img_url || student_profile}
                    alt="Student Avatar"
                  />
                  <label
                    htmlFor={`student${student.student_id}`}
                    onClick={() => handleCheck(student.student_id)}
                    style={{ cursor: "pointer" }}
                  >
                    {student.student_name}
                  </label>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="error-message-overviewperformence-dashboard">
            <div className="error-message-overviewperformence-dashboard-image">
              <img src={Nostudentinvite} alt="No students found" />
            </div>
            <p>No students available yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSelectfilter;
